import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessBoringBucket } from '../../lib/entitlements';
import '../Dashboard.css';

// The Boring Bucket capital-allocation framework. Principles are durable here;
// per-account numbers are set live in mentoring.
const RULES: { title: string; body: string; note?: string }[] = [
  {
    title: 'Buying power: 30% target, 50% hard ceiling',
    body:
      'Aim to keep buying-power usage around 30% of the account. It is a soft ' +
      'target - it flexes with market conditions and fills, and you never force ' +
      'it. But 50% is a hard ceiling you do not cross. The discipline is staying ' +
      'low: 30% is a target, never a floor to fill up to. Keeping most of the ' +
      'account uncommitted is what makes the method boring on purpose.',
    note:
      'Measure that 30% against your trading positions - the spreads and ' +
      'cash-secured puts - not tastytrade’s account-level buying-power figure. ' +
      'If your cash buffer is in SGOV, the account figure also includes the ' +
      'SGOV (a marginable security, roughly a 50% buying-power effect), so the ' +
      'raw number reads higher than your real trading usage.',
  },
  {
    title: 'Three engines: ES, MES, SPY',
    body:
      'The strategy runs across three engines - ES, MES, and SPY. The 30% ' +
      'buying-power target is divided across the three. Your per-engine sizing ' +
      'depends on your account size and is set with you in mentoring.',
  },
  {
    title: 'Defined-risk and cash-secured structures only',
    body:
      'Every position has a known, defined maximum loss: credit spreads and ' +
      'cash-secured puts. No uncovered naked selling, no long-debit speculation, ' +
      'no chasing. You always know your worst case before you enter.',
  },
  {
    title: 'You trade your own account',
    body:
      'You place your own trades in your own tastytrade account, mirroring the ' +
      'live Founder’s Trades at your discretion. You see exactly what is ' +
      'being traded and decide what fits your account and your risk. ' +
      'Auto-trading is offered selectively to some members - it is not a ' +
      'standard feature; if it is a fit for you, it is discussed individually.',
  },
  {
    title: 'Put the cash buffer to work',
    body:
      'The part of the account not used as buying power should not sit idle - ' +
      'tastytrade pays roughly 0.01% on cash. The framework suggests parking the ' +
      'buffer in short-term Treasuries: an ultra-short T-bill ETF (SGOV or BIL), ' +
      'or T-bills bought directly. Currently around 3.5-4%, near-zero ' +
      'volatility, and it still backs your trades as marginable collateral. ' +
      'This is a suggestion, not a requirement; each option carries its own ' +
      'small risks - understand them first.',
    note:
      'On margin treatment: ETFs like SGOV are marginable at the standard 50% ' +
      'stock rate. Treasuries bought directly get more favorable treatment (a ' +
      'small haircut), so they tie up less buying power - the trade-off is they ' +
      'are more hands-on, since you manage maturities. Either works. Keep it ' +
      'short-term either way - long-dated bonds carry real price risk and do ' +
      'not belong in the buffer.',
  },
  {
    title: 'Let the account double before you take profits',
    body:
      'Profits are not something to strip out early. The strong suggestion: ' +
      'leave them in the account and let it compound until it has doubled - at ' +
      'that point your original capital is effectively de-risked. After it ' +
      'doubles, what you do with profits is your call: many members move them ' +
      'into long-term equity ETFs (SCHD, VOO, QQQ) or withdraw. Your account, ' +
      'your decision. Keep this separate from the cash buffer - the buffer ' +
      'belongs in Treasury ETFs (stable), profits in equity ETFs (growth).',
  },
];

/**
 * Capital Plan - the capital allocation framework that defines the Boring
 * Bucket method. Member-gated. The numbers are walked through live in
 * mentoring; this page is the durable reference.
 */
export const CapitalPlan: React.FC = () => {
  const { user } = useAuth();
  const isMember = canAccessBoringBucket(user as any);

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content">
        <div style={{ padding: '1.5rem' }}>
          <div className="welcome-section">
            <h2>Capital Plan</h2>
            <p>The capital allocation framework - how the account is sized, run, and compounded.</p>
            <Link to="/boring-bucket" style={{ fontSize: 13, color: '#60a5fa' }}>&larr; Boring Bucket</Link>
          </div>

          {!isMember ? (
            <div style={panel}>
              <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Members only</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: 0 }}>
                The Capital Plan is part of The Boring Bucket membership.{' '}
                <Link to="/boring-bucket" style={{ color: '#60a5fa' }}>Learn more &rarr;</Link>
              </p>
            </div>
          ) : (
            <>
              {RULES.map((r) => (
                <Rule key={r.title} title={r.title} body={r.body} note={r.note} />
              ))}
              <div style={{ ...panel, marginTop: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Calculating your BP usage</h3>
                <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, margin: '0 0 8px' }}>
                  Measure the 30% from your trades, not tastytrade's account-level number.
                  Each order ticket shows an "Est. BP Effect" - your trading BP used is the
                  sum of those across your open trading positions. Exclude the cash buffer
                  (SGOV or T-bills); it is not a trade. Then: BP usage = trading BP used
                  divided by account net liquidating value.
                </p>
                <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                  Example - a $25,000 account, 30% target = $7,500. The buffer in SGOV
                  shows a ~$8,500 BP effect; your open trades' BP effects sum to $6,500.
                  Your usage is $6,500 / $25,000 = 26% - within target - even though
                  tastytrade's account screen reads ~60% because it includes the buffer.
                  Read your trades, not the account total.
                </p>
              </div>
              <div style={{ ...panel, marginTop: 14 }}>
                <h3 style={{ margin: '0 0 8px', color: '#f9fafb' }}>Your numbers</h3>
                <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>
                  Your account-specific sizing - buying power per engine, the cash buffer,
                  and how you handle profits - is worked through with you in your first
                  mentoring session and revisited as your account grows.
                </p>
              </div>
              <p style={{ color: '#64748b', fontSize: 11, marginTop: 16, lineHeight: 1.6 }}>
                Educational framework only - not individualized investment advice. SGOV,
                BIL, SCHD, VOO, QQQ and any other security named here are examples, not
                recommendations; each carries its own risk. You run your own account and
                make your own decisions. The Boring Bucket is not a registered investment
                advisor.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Rule: React.FC<{ title: string; body: string; note?: string }> = ({ title, body, note }) => (
  <div style={{ ...panel, marginBottom: 12 }}>
    <h3 style={{ margin: '0 0 6px', color: '#f9fafb', fontSize: '1.02em' }}>{title}</h3>
    <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.7, margin: 0 }}>{body}</p>
    {note && (
      <p style={{
        color: '#64748b', fontSize: 12, lineHeight: 1.6, margin: '8px 0 0',
        paddingLeft: 10, borderLeft: '2px solid rgba(96,165,250,0.4)',
      }}>
        {note}
      </p>
    )}
  </div>
);

const panel: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '20px 22px',
};

export default CapitalPlan;
